import {
  Box,
  Theme,
  createStyles,
  makeStyles,
  Typography,
} from '@material-ui/core';
import colors from '../../../themes/main-colors';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from '../../../hooks/useTranslation';
import PlantIcon from '../../../static/plant.svg';
import { Link } from 'gatsby';
import ROUTES from '../../../routes';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import OfflineBoltOutlinedIcon from '@material-ui/icons/OfflineBoltOutlined';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import useTPA from '../../need-more-time-to-pay/tpa/useTPA';
import { useApolloClient } from '@apollo/react-hooks';
import { getAccountDetailsForPaymentExtension } from '../../../queries/paymentExtension.query';
import {
  AccountDetail,
  PaymentExtensionEligibility,
  PaymentExtensionResponse,
} from '../../../__generated__/pge-types';
import { useAccountCustomerDashboard } from '../../../hooks/useAccountCustomerDashboard';
import useSelectedAccountParams from '../../../hooks/useSelectedAccountParams';
import { useIsMobile } from '../../../util/style-utils';
import {
  LanguageContext,
  SupportedLanguages,
} from '../../../providers/LanguageProvider';
import BillIcon from '../../../../src/static/bill-icon.svg';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    blueSection: {
      background: colors.navBarHoverBackground,
      borderRadius: '3px',
    },
    blueSectionMob: {
      background: colors.navBarHoverBackground,
      borderRadius: '3px',
      paddingTop: '2px',
      paddingBottom: '2px',
    },
    whiteSection: {
      background: colors.white,
      margin: '1rem',
      borderRadius: '3px',
      display: 'flex',
      flexDirection: 'column',
      [theme.breakpoints.down('sm')]: {
        margin: '0.5rem',
      },
    },
    graySection: {
      background: colors.cloudGrey,
      margin: '1rem',
      paddingTop: '1rem',
      paddingLeft: '1rem',
      paddingRight: '1rem',
      borderRadius: '3px',
      '& h6': {
        marginBottom: '-1rem',
        fontSize: '16px',
      },
      [theme.breakpoints.down('sm')]: {
        paddingTop: '0.5rem',
      },
    },
    link: {
      color: colors.sparkBlue,
      textDecoration: 'none',
    },
    icon: {
      color: colors.sparkBlue,
    },
    chevronRightIconStyles: {
      display: 'flex',
      justifyContent: 'space-between',
      width: '90%',
      paddingBottom: '0.5rem',
      marginLeft: '1rem',
      color: colors.noirBlur,
      '&.MuiTypography-body1': {
        fontSize: 16,
        fontFamily: 'Untitled-Sans',
      },
    },
    buttonGroup: {
      marginBottom: '1rem',
      [theme.breakpoints.down('sm')]: {
        marginBottom: '0.5rem',
      },
    },
    buttonStyles: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      backgroundColor: colors.white,
      marginLeft: '1rem',
      marginRight: '1rem',
      marginTop: '0.3rem',
      borderRadius: '5px',
      height: '40px',
      padding: '0 16px',
      border: '1px solid transparent',
      '&:hover': {
        borderColor: '#e5eff3',
      },
      '&.MuiTypography-subtitle2': {
        fontSize: 16,
      },
      [theme.breakpoints.down('sm')]: {
        marginLeft: '0.5rem',
        marginRight: '0.5rem',
      },
    },
  }),
);

export const EnergySavingsTips = () => {
  const classes = useStyles();
  const { t, richT } = useTranslation();
  const isMobile = useIsMobile();
  const { tpaDetails } = useTPA({ shouldRedirect: false });
  const apolloClient = useApolloClient();
  const { isResAccount } = useAccountCustomerDashboard();
  const { accountParams } = useSelectedAccountParams();
  const [paymentExtentionData, setPaymentExtentionData] = useState<
    PaymentExtensionResponse | undefined
  >();
  const { language } = useContext(LanguageContext);

  useEffect(() => {
    const fetchPaymentExtensionDetails = async () => {
      const result = await apolloClient.query<{
        getAccountDetails: Array<AccountDetail>;
      }>({
        query: getAccountDetailsForPaymentExtension,
        variables: {
          params: {
            accountNumberList: [accountParams],
          },
        },
        fetchPolicy: 'network-only',
      });
      const paymentExtensionInfo =
        result?.data?.getAccountDetails?.[0]?.paymentExtensionInfo;
      setPaymentExtentionData(paymentExtensionInfo!);
    };
    isResAccount && void fetchPaymentExtensionDetails();
  }, [isResAccount]);

  const title = isResAccount
    ? richT('EASY_ENERGY_SAVING_TIPS')
    : richT('EASY_ENERGY_SAVING_TIPS_COMMERCIAL');
  const subText = isResAccount
    ? richT('OPTIMIZE_YOUR_ENERY_TEXT')
    : richT('OPTIMIZE_YOUR_ENERY_TEXT_COMMERCIAL');

  return (
    <>
      <div className={isMobile ? classes.blueSectionMob : classes.blueSection}>
        <div className={classes.whiteSection}>
          <Box className={classes.graySection}>
            <Typography variant="h3" style={{ paddingTop: '1px' }}>
              {title}
            </Typography>
            <Typography
              variant="subtitle1"
              style={{ marginBottom: '0px !important' }}
            >
              {subText}
            </Typography>
            <div style={{ justifyContent: 'flex-end', display: 'flex' }}>
              <PlantIcon />
            </div>
          </Box>
          <Link style={{ textDecoration: 'none' }} to={ROUTES.MY_WAYS_TO_SAVE}>
            <div className={classes.chevronRightIconStyles}>
              <Typography>{t('MY_WAYS_TO_SAVE')}</Typography>

              <ChevronRightIcon />
            </div>
          </Link>
        </div>
        <Box className={classes.buttonGroup}>
          <Link to={ROUTES.PAYMENT_HISTORY} className={classes.link}>
            <div className={classes.buttonStyles}>
              <Typography variant="subtitle2">
                {t('VIEW_BILL_HISTORY')}
              </Typography>

              <BillIcon style={{ height: '24px' }} className={classes.icon} />
            </div>
          </Link>
          <Link to={ROUTES.MY_ENERGY_USE} className={classes.link}>
            <div className={classes.buttonStyles}>
              <Typography variant="subtitle2">
                {t('MY_ENERGY_USE_DETAILS')}
              </Typography>

              <OfflineBoltOutlinedIcon className={classes.icon} />
            </div>
          </Link>
          {(tpaDetails
            ? tpaDetails?.isEnrolled ||
              tpaDetails?.isEligible ||
              (paymentExtentionData &&
                (paymentExtentionData?.eligibility! !==
                  PaymentExtensionEligibility.Ineligible ||
                  paymentExtentionData?.isEnrolled!))
            : false) && (
            <Link to={ROUTES.NEED_MORE_TIME_TO_PAY} className={classes.link}>
              {' '}
              <div className={classes.buttonStyles}>
                <Typography variant="subtitle2">{t('NMTTP')}</Typography>

                <AccessTimeIcon className={classes.icon} />
              </div>
            </Link>
          )}
          <Link
            to={
              language === SupportedLanguages.English
                ? ROUTES.UNDERSTANDING_MY_BILL
                : language === SupportedLanguages.Spanish
                ? ROUTES.UNDERSTANDING_MY_BILL_SPANISH
                : ROUTES.UNDERSTANDING_MY_BILL
            }
            className={classes.link}
          >
            <div className={classes.buttonStyles}>
              <Typography variant="subtitle2">
                {t('UNDERSTANDING_MY_BILL')}
              </Typography>

              <InfoOutlinedIcon
                style={{ height: '24px' }}
                className={classes.icon}
              />
            </div>
          </Link>
        </Box>
      </div>
    </>
  );
};
